import {
  ARENA_RADIUS,
  FIXED_STEP,
  INITIAL_POWER,
  RELEASE_VERSION,
  RUN_DURATION,
  SYSTEMS,
  TOTAL_POWER,
  waveForTime,
} from "../core/config";
import { SeededRandom } from "../core/random";
import type {
  EnemyState,
  EnemyType,
  GameEvent,
  GamePhase,
  GameSnapshot,
  InputIntent,
  OnboardingState,
  PickupState,
  PlayerState,
  PowerAllocation,
  ProjectileState,
  SystemId,
  Vector2,
} from "../core/types";

const TAU = Math.PI * 2;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function length(vector: Vector2): number {
  return Math.hypot(vector.x, vector.z);
}

function distance(a: Vector2, b: Vector2): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

function normalize(vector: Vector2): Vector2 {
  const magnitude = length(vector);
  if (magnitude < 0.0001) return { x: 0, z: 0 };
  return { x: vector.x / magnitude, z: vector.z / magnitude };
}

function copyPower(power: PowerAllocation): PowerAllocation {
  return { ...power };
}

function copyPlayer(player: PlayerState): PlayerState {
  return {
    ...player,
    position: { ...player.position },
    velocity: { ...player.velocity },
    power: copyPower(player.power),
  };
}

function copyEnemy(enemy: EnemyState): EnemyState {
  return {
    ...enemy,
    position: { ...enemy.position },
    velocity: { ...enemy.velocity },
  };
}

function copyProjectile(projectile: ProjectileState): ProjectileState {
  return {
    ...projectile,
    position: { ...projectile.position },
    velocity: { ...projectile.velocity },
  };
}

function copyPickup(pickup: PickupState): PickupState {
  return { ...pickup, position: { ...pickup.position } };
}

export function powerTotal(power: PowerAllocation): number {
  return SYSTEMS.reduce((total, system) => total + power[system], 0);
}

export function isValidPower(power: PowerAllocation): boolean {
  return (
    powerTotal(power) === TOTAL_POWER &&
    power.propulsion >= 1 &&
    SYSTEMS.every((system) => power[system] >= 0 && power[system] <= 4)
  );
}

export function calculateFinalScore(
  score: number,
  hull: number,
  remainingTime: number,
  won: boolean,
): number {
  const survivalBonus = Math.round(Math.max(0, hull) * 12);
  const clearBonus = won ? 2_500 + Math.round(Math.max(0, remainingTime) * 25) : 0;
  return Math.max(0, Math.round(score + survivalBonus + clearBonus));
}

export class GameSimulation {
  readonly seed: number;

  private random: SeededRandom;
  private phase: GamePhase = "menu";
  private time = 0;
  private wave = 1;
  private bossPhase = 0;
  private score = 0;
  private kills = 0;
  private player: PlayerState;
  private enemies: EnemyState[] = [];
  private projectiles: ProjectileState[] = [];
  private pickups: PickupState[] = [];
  private events: GameEvent[] = [];
  private pendingEvents: GameEvent[] = [];
  private nextEntityId = 1;
  private nextSpawnTime = 1.5;
  private bossSpawned = false;
  private waveDamageTaken = 0;
  private accumulated = 0;
  private pendingSonar = false;
  private onboarding: OnboardingState = {
    moved: false,
    routed: false,
    pulsed: false,
  };
  private onboardingCompleteEmitted = false;

  constructor(seed: number) {
    this.seed = seed >>> 0 || 731_993;
    this.random = new SeededRandom(this.seed);
    this.player = this.createPlayer();
  }

  private createPlayer(): PlayerState {
    const maxShield = 15 + INITIAL_POWER.shield * 18;
    return {
      position: { x: 0, z: 0 },
      velocity: { x: 0, z: 0 },
      heading: Math.PI,
      hull: 100,
      shield: maxShield,
      maxShield,
      sonarCooldown: 0,
      relayCooldown: 0,
      weaponCooldown: 0,
      invulnerableFor: 0,
      combo: 0,
      comboTimer: 0,
      highestCombo: 0,
      overdriveFor: 0,
      power: copyPower(INITIAL_POWER),
    };
  }

  start(): void {
    this.random = new SeededRandom(this.seed);
    this.phase = "playing";
    this.time = 0;
    this.wave = 1;
    this.bossPhase = 0;
    this.score = 0;
    this.kills = 0;
    this.player = this.createPlayer();
    this.enemies = [];
    this.projectiles = [];
    this.pickups = [];
    this.events = [];
    this.pendingEvents = [];
    this.nextEntityId = 1;
    this.nextSpawnTime = 5.5;
    this.bossSpawned = false;
    this.waveDamageTaken = 0;
    this.accumulated = 0;
    this.pendingSonar = false;
    this.onboarding = { moved: false, routed: false, pulsed: false };
    this.onboardingCompleteEmitted = false;
    this.emit("run_started");
    this.emit("wave_started", { wave: 1 });
  }

  restart(): void {
    this.start();
    this.emit("run_restarted");
  }

  pause(): void {
    if (this.phase === "playing") this.phase = "paused";
  }

  resume(): void {
    if (this.phase === "paused") this.phase = "playing";
  }

  routePower(target: SystemId): boolean {
    if (this.phase !== "playing" || this.player.relayCooldown > 0) return false;
    if (this.player.power[target] >= 4) return false;

    const donors = SYSTEMS.filter((system) => {
      if (system === target) return false;
      const minimum = system === "propulsion" ? 1 : 0;
      return this.player.power[system] > minimum;
    }).sort((a, b) => this.player.power[b] - this.player.power[a]);

    const donor = donors[0];
    if (!donor) return false;

    this.player.power[donor] -= 1;
    this.player.power[target] += 1;
    this.player.relayCooldown = 1.25;
    this.onboarding.routed = true;
    this.checkOnboarding();
    this.emit("system_routed", { from: donor, to: target });
    return true;
  }

  pulseSonar(): boolean {
    if (this.phase !== "playing" || this.player.sonarCooldown > 0) return false;
    const power = this.player.power.sonar;
    const radius = 11 + power * 4.5;
    const revealDuration = 2.5 + power * 1.2;

    for (const enemy of this.enemies) {
      if (distance(enemy.position, this.player.position) <= radius) {
        enemy.revealedFor = Math.max(enemy.revealedFor, revealDuration);
      }
    }

    this.player.sonarCooldown = Math.max(2.4, 9 - power * 1.3);
    this.onboarding.pulsed = true;
    this.checkOnboarding();
    this.emit("sonar_pulse", { radius });
    return true;
  }

  advanceWallTime(delta: number, input: InputIntent): void {
    if (this.phase !== "playing") return;
    this.pendingSonar ||= input.sonar;
    this.accumulated = Math.min(this.accumulated + delta, 0.25);
    while (this.accumulated >= FIXED_STEP && this.phase === "playing") {
      this.stepFixed(FIXED_STEP, {
        ...input,
        sonar: this.pendingSonar,
      });
      this.pendingSonar = false;
      this.accumulated -= FIXED_STEP;
    }
  }

  advanceFixed(seconds: number, input: InputIntent = { steerX: 0, steerZ: 0, sonar: false }): void {
    const steps = Math.max(0, Math.floor(seconds / FIXED_STEP));
    for (let index = 0; index < steps && this.phase === "playing"; index += 1) {
      this.stepFixed(FIXED_STEP, input);
    }
  }

  private stepFixed(delta: number, input: InputIntent): void {
    const previousWave = this.wave;
    this.time += delta;
    this.wave = waveForTime(this.time);

    if (this.wave !== previousWave) {
      if (this.waveDamageTaken === 0) this.score += 350 * previousWave;
      this.waveDamageTaken = 0;
      this.emit("wave_started", { wave: this.wave });
    }

    if (this.wave === 4 && !this.bossSpawned) this.spawnBoss();

    this.updateCooldowns(delta);
    this.updatePlayer(delta, input);
    this.spawnEnemies();
    this.updateEnemies(delta);
    this.updateWeapons();
    this.updateProjectiles(delta);
    this.updatePickups(delta);
    this.resolveContactDamage();
    this.resolveBossPhase();

    if (this.player.hull <= 0) this.finish(false);
    if (this.time >= RUN_DURATION && this.phase === "playing") this.finish(false);
  }

  private updateCooldowns(delta: number): void {
    this.player.sonarCooldown = Math.max(0, this.player.sonarCooldown - delta);
    this.player.relayCooldown = Math.max(0, this.player.relayCooldown - delta);
    this.player.weaponCooldown = Math.max(0, this.player.weaponCooldown - delta);
    this.player.invulnerableFor = Math.max(0, this.player.invulnerableFor - delta);
    this.player.comboTimer = Math.max(0, this.player.comboTimer - delta);
    if (this.player.comboTimer <= 0) this.player.combo = 0;
    const hadOverdrive = this.player.overdriveFor > 0;
    this.player.overdriveFor = Math.max(0, this.player.overdriveFor - delta);
    if (hadOverdrive && this.player.overdriveFor <= 0) this.emit("overdrive_ended");

    for (const enemy of this.enemies) {
      enemy.revealedFor = Math.max(0, enemy.revealedFor - delta);
      enemy.attackCooldown = Math.max(0, enemy.attackCooldown - delta);
    }

    const desiredMaxShield = 15 + this.player.power.shield * 18;
    this.player.maxShield = desiredMaxShield;
    this.player.shield = Math.min(this.player.shield, desiredMaxShield);
    if (this.player.invulnerableFor <= 0 && this.player.power.shield > 0) {
      const recharge = 0.9 + this.player.power.shield * 1.6;
      this.player.shield = Math.min(desiredMaxShield, this.player.shield + recharge * delta);
    }

    if (this.player.power.repair >= 2 && this.player.hull < 100) {
      const repairRate = (this.player.power.repair - 1) * 0.65;
      const before = this.player.hull;
      this.player.hull = Math.min(100, this.player.hull + repairRate * delta);
      if (Math.floor(before) < Math.floor(this.player.hull) && Math.floor(this.player.hull) % 5 === 0) {
        this.emit("repair_completed", { hull: Math.floor(this.player.hull) });
      }
    }
  }

  private updatePlayer(delta: number, input: InputIntent): void {
    if (input.sonar) this.pulseSonar();

    const desired = normalize({ x: clamp(input.steerX, -1, 1), z: clamp(input.steerZ, -1, 1) });
    if (!this.onboarding.moved && length(desired) > 0.25) {
      this.onboarding.moved = true;
      this.checkOnboarding();
    }
    const surgeSpeed = this.player.overdriveFor > 0 ? 1.2 : 0;
    const acceleration = 4.5 + this.player.power.propulsion * 2.2 + surgeSpeed;
    const topSpeed = 3.4 + this.player.power.propulsion * 1.05 + surgeSpeed;

    this.player.velocity.x += desired.x * acceleration * delta;
    this.player.velocity.z += desired.z * acceleration * delta;

    const damping = Math.pow(0.25, delta);
    this.player.velocity.x *= damping;
    this.player.velocity.z *= damping;

    const velocityLength = length(this.player.velocity);
    if (velocityLength > topSpeed) {
      const factor = topSpeed / velocityLength;
      this.player.velocity.x *= factor;
      this.player.velocity.z *= factor;
    }

    this.player.position.x += this.player.velocity.x * delta;
    this.player.position.z += this.player.velocity.z * delta;

    const radial = length(this.player.position);
    if (radial > ARENA_RADIUS - 1.5) {
      const normal = normalize(this.player.position);
      this.player.position.x = normal.x * (ARENA_RADIUS - 1.5);
      this.player.position.z = normal.z * (ARENA_RADIUS - 1.5);
      this.player.velocity.x *= -0.25;
      this.player.velocity.z *= -0.25;
    }

    if (velocityLength > 0.15) {
      const targetHeading = Math.atan2(this.player.velocity.x, this.player.velocity.z);
      let difference = targetHeading - this.player.heading;
      while (difference > Math.PI) difference -= TAU;
      while (difference < -Math.PI) difference += TAU;
      const turnRate = 2.1 + this.player.power.propulsion * 0.45;
      this.player.heading += clamp(difference, -turnRate * delta, turnRate * delta);
    }
  }

  private spawnEnemies(): void {
    if (this.wave === 4 || this.time < this.nextSpawnTime || this.enemies.length >= 22) return;

    const count = this.wave === 1 ? 1 : this.random.int(1, Math.min(3, this.wave));
    for (let index = 0; index < count; index += 1) {
      const type = this.pickEnemyType();
      this.enemies.push(this.createEnemy(type));
    }

    const interval = Math.max(1.3, 4.8 - this.wave * 0.8);
    this.nextSpawnTime = this.time + this.random.range(interval * 0.8, interval * 1.15);
  }

  private pickEnemyType(): EnemyType {
    if (this.wave === 1) return this.random.pick(["drifter", "drifter", "mine"] as const);
    if (this.wave === 2) {
      return this.random.pick(["drifter", "stalker", "stalker", "mine"] as const);
    }
    return this.random.pick(["drifter", "stalker", "mine", "ray", "ray"] as const);
  }

  private createEnemy(type: EnemyType): EnemyState {
    const angle = this.random.range(0, TAU);
    const radiusFromCenter = this.random.range(22, 29);
    const stats: Record<EnemyType, { hull: number; radius: number }> = {
      drifter: { hull: 22 + this.wave * 4, radius: 0.75 },
      stalker: { hull: 30 + this.wave * 5, radius: 0.9 },
      mine: { hull: 18 + this.wave * 3, radius: 0.8 },
      ray: { hull: 36 + this.wave * 6, radius: 1.1 },
      leviathan: { hull: 560, radius: 3.8 },
    };
    const stat = stats[type];

    return {
      id: this.nextEntityId++,
      type,
      position: {
        x: Math.sin(angle) * radiusFromCenter,
        z: Math.cos(angle) * radiusFromCenter,
      },
      velocity: { x: 0, z: 0 },
      heading: angle + Math.PI,
      hull: stat.hull,
      maxHull: stat.hull,
      radius: stat.radius,
      revealedFor: type === "mine" ? 0 : 0.8,
      attackCooldown: this.random.range(0.2, 1.2),
      phase: 0,
    };
  }

  private spawnBoss(): void {
    this.bossSpawned = true;
    const boss = this.createEnemy("leviathan");
    boss.position = { x: 0, z: -14 };
    boss.revealedFor = RUN_DURATION;
    boss.attackCooldown = 2.5;
    this.enemies.push(boss);
    this.emit("boss_spawned", { id: boss.id });
  }

  private updateEnemies(delta: number): void {
    for (const enemy of this.enemies) {
      const toPlayer = {
        x: this.player.position.x - enemy.position.x,
        z: this.player.position.z - enemy.position.z,
      };
      const playerDistance = Math.max(0.001, length(toPlayer));
      const direction = normalize(toPlayer);
      let speed = 0;
      let desired = direction;

      if (enemy.type === "drifter") {
        speed = 1.5 + this.wave * 0.22;
      } else if (enemy.type === "stalker") {
        const tangent = { x: -direction.z, z: direction.x };
        const inward = playerDistance > 8 ? 0.65 : -0.2;
        desired = normalize({
          x: tangent.x + direction.x * inward,
          z: tangent.z + direction.z * inward,
        });
        speed = enemy.attackCooldown <= 0 && playerDistance < 10 ? 5.8 : 2.2;
      } else if (enemy.type === "mine") {
        speed = playerDistance < 9 ? 1.2 : 0.25;
      } else if (enemy.type === "ray") {
        const rangeCorrection = playerDistance > 13 ? 0.8 : playerDistance < 9 ? -0.8 : 0;
        const tangent = { x: -direction.z, z: direction.x };
        desired = normalize({
          x: tangent.x * 0.75 + direction.x * rangeCorrection,
          z: tangent.z * 0.75 + direction.z * rangeCorrection,
        });
        speed = 2.0;
        if (enemy.attackCooldown <= 0 && playerDistance < 18) {
          this.fireEnemyProjectile(enemy, direction, 9 + this.wave * 2);
          enemy.attackCooldown = Math.max(1.8, 4.4 - this.wave * 0.55);
        }
      } else {
        const orbit = { x: -direction.z, z: direction.x };
        const rangeCorrection = playerDistance > 14 ? 0.6 : playerDistance < 10 ? -0.7 : 0;
        desired = normalize({
          x: orbit.x + direction.x * rangeCorrection,
          z: orbit.z + direction.z * rangeCorrection,
        });
        speed = 1.1 + enemy.phase * 0.35;
        if (enemy.attackCooldown <= 0) {
          const shots = 3 + enemy.phase * 2;
          for (let index = 0; index < shots; index += 1) {
            const spread = (index - (shots - 1) / 2) * 0.14;
            const cosine = Math.cos(spread);
            const sine = Math.sin(spread);
            const shotDirection = {
              x: direction.x * cosine - direction.z * sine,
              z: direction.x * sine + direction.z * cosine,
            };
            this.fireEnemyProjectile(enemy, shotDirection, 8 + enemy.phase * 2);
          }
          enemy.attackCooldown = Math.max(1.2, 3.4 - enemy.phase * 0.7);
        }
      }

      const response = Math.min(1, delta * 3.5);
      enemy.velocity.x += (desired.x * speed - enemy.velocity.x) * response;
      enemy.velocity.z += (desired.z * speed - enemy.velocity.z) * response;
      enemy.position.x += enemy.velocity.x * delta;
      enemy.position.z += enemy.velocity.z * delta;
      enemy.heading = Math.atan2(enemy.velocity.x, enemy.velocity.z);
    }
  }

  private updateWeapons(): void {
    const weaponPower = this.player.power.weapons;
    if (weaponPower <= 0 || this.player.weaponCooldown > 0) return;

    const forward = {
      x: Math.sin(this.player.heading),
      z: Math.cos(this.player.heading),
    };
    const range = 12 + weaponPower * 2.6;
    let target: EnemyState | undefined;
    let targetDistance = Number.POSITIVE_INFINITY;

    for (const enemy of this.enemies) {
      const offset = {
        x: enemy.position.x - this.player.position.x,
        z: enemy.position.z - this.player.position.z,
      };
      const enemyDistance = length(offset);
      const direction = normalize(offset);
      const dot = direction.x * forward.x + direction.z * forward.z;
      const visible = enemy.revealedFor > 0 || enemy.type === "leviathan" || enemyDistance < 7;
      if (visible && dot > -0.1 && enemyDistance < range && enemyDistance < targetDistance) {
        target = enemy;
        targetDistance = enemyDistance;
      }
    }

    if (!target) return;
    const direction = normalize({
      x: target.position.x - this.player.position.x,
      z: target.position.z - this.player.position.z,
    });
    const overdrive = this.player.overdriveFor > 0;
    const shotCount = overdrive ? 3 : 1;
    const damage = (4 + weaponPower * 2.8) * (overdrive ? 1.25 : 1);
    for (let index = 0; index < shotCount; index += 1) {
      const spread = (index - (shotCount - 1) / 2) * 0.09;
      const cosine = Math.cos(spread);
      const sine = Math.sin(spread);
      const shotDirection = {
        x: direction.x * cosine - direction.z * sine,
        z: direction.x * sine + direction.z * cosine,
      };
      this.projectiles.push({
        id: this.nextEntityId++,
        owner: "player",
        position: {
          x: this.player.position.x + shotDirection.x * 0.9,
          z: this.player.position.z + shotDirection.z * 0.9,
        },
        velocity: {
          x: shotDirection.x * (overdrive ? 30 : 24),
          z: shotDirection.z * (overdrive ? 30 : 24),
        },
        damage,
        lifetime: overdrive ? 1.15 : 1.4,
        radius: overdrive ? 0.28 : 0.22,
        variant: overdrive ? "surge" : "torpedo",
      });
    }
    this.emit("weapon_fired", {
      x: this.player.position.x,
      z: this.player.position.z,
      heading: this.player.heading,
      shots: shotCount,
      overdrive,
    });
    this.player.weaponCooldown = overdrive
      ? 0.13
      : Math.max(0.24, 0.9 - weaponPower * 0.13);
  }

  private fireEnemyProjectile(enemy: EnemyState, direction: Vector2, damage: number): void {
    this.projectiles.push({
      id: this.nextEntityId++,
      owner: "enemy",
      position: { ...enemy.position },
      velocity: { x: direction.x * 7.5, z: direction.z * 7.5 },
      damage,
      lifetime: 4,
      radius: enemy.type === "leviathan" ? 0.5 : 0.3,
      variant: enemy.type === "leviathan" ? "void" : "plasma",
    });
  }

  private updateProjectiles(delta: number): void {
    const remaining: ProjectileState[] = [];

    for (const projectile of this.projectiles) {
      projectile.position.x += projectile.velocity.x * delta;
      projectile.position.z += projectile.velocity.z * delta;
      projectile.lifetime -= delta;
      let hit = false;

      if (projectile.owner === "player") {
        for (const enemy of this.enemies) {
          if (distance(projectile.position, enemy.position) <= projectile.radius + enemy.radius) {
            const reduction = enemy.type === "leviathan" && enemy.phase === 0 ? 0.62 : 1;
            const appliedDamage = projectile.damage * reduction;
            enemy.hull -= appliedDamage;
            this.score += Math.round(appliedDamage * 0.7);
            this.emit("enemy_hit", {
              id: enemy.id,
              enemyType: enemy.type,
              x: projectile.position.x,
              z: projectile.position.z,
              damage: Math.round(appliedDamage),
              surge: projectile.variant === "surge",
            });
            hit = true;
            break;
          }
        }
      } else if (
        distance(projectile.position, this.player.position) <=
        projectile.radius + 0.85
      ) {
        this.damagePlayer(projectile.damage);
        hit = true;
      }

      if (!hit && projectile.lifetime > 0) remaining.push(projectile);
    }

    this.projectiles = remaining;

    const survivors: EnemyState[] = [];
    for (const enemy of this.enemies) {
      if (enemy.hull <= 0) {
        this.destroyEnemy(enemy);
      } else {
        survivors.push(enemy);
      }
    }
    this.enemies = survivors;
  }

  private destroyEnemy(enemy: EnemyState): void {
    const baseScore: Record<EnemyType, number> = {
      drifter: 80,
      stalker: 130,
      mine: 90,
      ray: 170,
      leviathan: 2_000,
    };
    this.player.combo = this.player.comboTimer > 0 ? this.player.combo + 1 : 1;
    this.player.comboTimer = 4.2;
    this.player.highestCombo = Math.max(this.player.highestCombo, this.player.combo);
    const multiplier = 1 + Math.min(4, this.player.combo - 1) * 0.25;
    const awarded = Math.round((baseScore[enemy.type] + this.wave * 15) * multiplier);
    this.score += awarded;
    this.kills += 1;
    this.emit("enemy_destroyed", {
      id: enemy.id,
      enemyType: enemy.type,
      x: enemy.position.x,
      z: enemy.position.z,
      combo: this.player.combo,
      awarded,
    });
    this.emit("combo_changed", { combo: this.player.combo, awarded });

    if (enemy.type === "leviathan") {
      this.finish(true);
      return;
    }

    if (this.player.combo === 5) {
      this.pickups.push({
        id: this.nextEntityId++,
        position: { ...enemy.position },
        kind: "surge",
        lifetime: 18,
      });
    } else if (this.random.next() < 0.28) {
      const roll = this.random.next();
      this.pickups.push({
        id: this.nextEntityId++,
        position: { ...enemy.position },
        kind: roll < 0.42 ? "repair" : roll < 0.72 ? "shield" : "score",
        lifetime: 14,
      });
    }
  }

  private updatePickups(delta: number): void {
    const remaining: PickupState[] = [];
    for (const pickup of this.pickups) {
      pickup.lifetime -= delta;
      const pickupDistance = distance(pickup.position, this.player.position);
      if (pickupDistance < 6.5 && pickupDistance > 0.01) {
        const direction = normalize({
          x: this.player.position.x - pickup.position.x,
          z: this.player.position.z - pickup.position.z,
        });
        const magnetSpeed = pickup.kind === "surge" ? 10 : 6.5;
        pickup.position.x += direction.x * magnetSpeed * delta;
        pickup.position.z += direction.z * magnetSpeed * delta;
      }
      if (distance(pickup.position, this.player.position) < 1.6) {
        if (pickup.kind === "repair") {
          this.player.hull = Math.min(100, this.player.hull + 18);
        } else if (pickup.kind === "shield") {
          this.player.shield = Math.min(this.player.maxShield, this.player.shield + 32);
        } else if (pickup.kind === "score") {
          this.score += 320;
        } else {
          this.player.overdriveFor = Math.max(this.player.overdriveFor, 8);
          this.player.shield = Math.min(this.player.maxShield, this.player.shield + 18);
          for (const enemy of this.enemies) {
            const blastDistance = distance(enemy.position, this.player.position);
            if (blastDistance <= 14) {
              enemy.hull -= enemy.type === "leviathan" ? 26 : 52;
            }
          }
          this.emit("overdrive_started", {
            duration: 8,
            x: this.player.position.x,
            z: this.player.position.z,
          });
        }
        this.emit("pickup_collected", { kind: pickup.kind });
      } else if (pickup.lifetime > 0) {
        remaining.push(pickup);
      }
    }
    this.pickups = remaining;
  }

  private resolveContactDamage(): void {
    for (const enemy of this.enemies) {
      if (
        enemy.attackCooldown <= 0 &&
        distance(enemy.position, this.player.position) < enemy.radius + 0.9
      ) {
        const damage =
          enemy.type === "mine"
            ? 28
            : enemy.type === "leviathan"
              ? 22
              : 8 + this.wave * 2;
        this.damagePlayer(damage);
        enemy.attackCooldown = enemy.type === "mine" ? 99 : 1.1;
        if (enemy.type === "mine") enemy.hull = 0;
      }
    }
  }

  private damagePlayer(amount: number): void {
    if (this.player.invulnerableFor > 0 || this.phase !== "playing") return;
    let remainingDamage = amount;
    if (this.player.shield > 0) {
      const absorbed = Math.min(this.player.shield, remainingDamage);
      this.player.shield -= absorbed;
      remainingDamage -= absorbed;
      if (this.player.shield <= 0) this.emit("shield_broken");
    }
    if (remainingDamage > 0) {
      this.player.hull = Math.max(0, this.player.hull - remainingDamage);
    }
    this.player.invulnerableFor = 0.28;
    this.waveDamageTaken += amount;
    this.emit("player_damaged", { amount, hull: this.player.hull });
  }

  private resolveBossPhase(): void {
    const boss = this.enemies.find((enemy) => enemy.type === "leviathan");
    if (!boss) return;
    const healthRatio = boss.hull / boss.maxHull;
    const nextPhase = healthRatio <= 0.34 ? 2 : healthRatio <= 0.7 ? 1 : 0;
    if (nextPhase !== boss.phase) {
      boss.phase = nextPhase;
      this.bossPhase = nextPhase;
      boss.attackCooldown = 0.6;
      this.emit("boss_phase_changed", { phase: nextPhase });
    }
  }

  private finish(won: boolean): void {
    if (this.phase === "won" || this.phase === "lost") return;
    this.phase = won ? "won" : "lost";
    this.score = calculateFinalScore(
      this.score,
      this.player.hull,
      RUN_DURATION - this.time,
      won,
    );
    this.emit(won ? "run_won" : "run_lost", {
      score: this.score,
      hull: this.player.hull,
      kills: this.kills,
      highestCombo: this.player.highestCombo,
    });
  }

  private checkOnboarding(): void {
    if (
      !this.onboardingCompleteEmitted &&
      this.onboarding.moved &&
      this.onboarding.routed &&
      this.onboarding.pulsed
    ) {
      this.onboardingCompleteEmitted = true;
      this.emit("onboarding_completed");
    }
  }

  private emit(type: GameEvent["type"], data?: GameEvent["data"]): void {
    const event: GameEvent = { type, time: this.time, seed: this.seed, data };
    this.events.push(event);
    this.pendingEvents.push(event);
    if (this.events.length > 240) this.events.shift();
  }

  consumeEvents(): GameEvent[] {
    const current = this.pendingEvents;
    this.pendingEvents = [];
    return current;
  }

  setQaScenario(name: "combat" | "barrage" | "boss" | "won" | "lost"): void {
    this.start();
    if (name === "combat") {
      this.time = 34;
      this.wave = 2;
      this.nextSpawnTime = RUN_DURATION;
      this.player.invulnerableFor = RUN_DURATION;
      this.player.heading = 0;
      this.player.power = {
        propulsion: 1,
        sonar: 1,
        shield: 2,
        repair: 0,
        weapons: 4,
      };
      this.player.combo = 4;
      this.player.comboTimer = 4.2;
      this.player.highestCombo = 4;
      this.player.overdriveFor = 8;
      this.onboarding = { moved: true, routed: true, pulsed: true };
      this.onboardingCompleteEmitted = true;
      const combatants: Array<[EnemyType, number, number]> = [
        ["drifter", 0, 6],
        ["stalker", -5, 8],
        ["mine", 4.5, 7.5],
        ["ray", 1.5, 11],
      ];
      this.enemies = combatants.map(([type, x, z]) => {
        const enemy = this.createEnemy(type);
        enemy.position = { x, z };
        enemy.revealedFor = RUN_DURATION;
        enemy.velocity = { x: 0, z: 0 };
        enemy.hull = 999;
        enemy.maxHull = 999;
        return enemy;
      });
      this.pickups.push({
        id: this.nextEntityId++,
        position: { x: 2.7, z: 2.2 },
        kind: "surge",
        lifetime: 18,
      });
      this.projectiles.push({
        id: this.nextEntityId++,
        owner: "player",
        position: { x: -3.2, z: 2.4 },
        velocity: { x: 0, z: 0 },
        damage: 0,
        lifetime: RUN_DURATION,
        radius: 0.28,
        variant: "surge",
      });
      this.emit("overdrive_started", { duration: 8, x: 0, z: 0 });
      return;
    }
    if (name === "barrage") {
      this.time = 112;
      this.wave = 3;
      this.nextSpawnTime = RUN_DURATION;
      this.player.invulnerableFor = RUN_DURATION;
      this.player.power = {
        propulsion: 2,
        sonar: 2,
        shield: 4,
        repair: 2,
        weapons: 0,
      };
      this.onboarding = { moved: true, routed: true, pulsed: true };
      this.onboardingCompleteEmitted = true;
      for (let index = 0; index < 120; index += 1) {
        const angle = (index / 120) * Math.PI * 2;
        const radius = 7 + (index % 6) * 1.15;
        this.projectiles.push({
          id: this.nextEntityId++,
          owner: "enemy",
          position: {
            x: Math.cos(angle) * radius,
            z: Math.sin(angle) * radius,
          },
          velocity: {
            x: -Math.sin(angle) * 1.8,
            z: Math.cos(angle) * 1.8,
          },
          damage: 0,
          lifetime: RUN_DURATION,
          radius: index % 5 === 0 ? 0.5 : 0.3,
          variant: index % 5 === 0 ? "void" : "plasma",
        });
      }
      return;
    }
    if (name === "boss") {
      this.time = 135;
      this.wave = 4;
      this.player.invulnerableFor = RUN_DURATION;
      this.player.power = {
        propulsion: 1,
        sonar: 2,
        shield: 3,
        repair: 0,
        weapons: 4,
      };
      this.onboarding = { moved: true, routed: true, pulsed: true };
      this.onboardingCompleteEmitted = true;
      this.spawnBoss();
      return;
    }
    if (name === "won") {
      this.time = 164;
      this.score = 5_400;
      this.kills = 26;
      this.finish(true);
      return;
    }
    this.time = 72;
    this.score = 1_180;
    this.kills = 8;
    this.player.hull = 0;
    this.finish(false);
  }

  snapshot(): GameSnapshot {
    return {
      version: RELEASE_VERSION,
      seed: this.seed,
      phase: this.phase,
      time: this.time,
      remainingTime: Math.max(0, RUN_DURATION - this.time),
      wave: this.wave,
      bossPhase: this.bossPhase,
      score: this.score,
      kills: this.kills,
      onboarding: { ...this.onboarding },
      player: copyPlayer(this.player),
      enemies: this.enemies.map(copyEnemy),
      pickups: this.pickups.map(copyPickup),
      projectiles: this.projectiles.map(copyProjectile),
      events: this.events.map((event) => ({ ...event, data: event.data ? { ...event.data } : undefined })),
    };
  }

  frameView(): GameSnapshot {
    return {
      version: RELEASE_VERSION,
      seed: this.seed,
      phase: this.phase,
      time: this.time,
      remainingTime: Math.max(0, RUN_DURATION - this.time),
      wave: this.wave,
      bossPhase: this.bossPhase,
      score: this.score,
      kills: this.kills,
      onboarding: this.onboarding,
      player: this.player,
      enemies: this.enemies,
      pickups: this.pickups,
      projectiles: this.projectiles,
      events: this.events,
    };
  }
}
